import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LuMessageSquare, LuX, LuStar, LuSend, LuCircleCheck, LuBug, LuLightbulb, LuSmile, LuSparkles } from 'react-icons/lu';
import { notifySuccess, notifyError } from './AppFeedbackCenter';
import feedbackService from '../services/feedbackService';
import useEscapeKey from '../hooks/useEscapeKey';
import './FeedbackWidget.css';

const CATEGORIES = [
  { id: 'bug', label: 'Bug Report', icon: LuBug },
  { id: 'feature', label: 'Feature Request', icon: LuLightbulb },
  { id: 'ux', label: 'UI / UX', icon: LuSparkles },
  { id: 'general', label: 'General Feedback', icon: LuSmile },
];

const FeedbackWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEscapeKey(() => setIsOpen(false), isOpen);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u.name) setUserName(u.name);
        if (u.email) setUserEmail(u.email);
      }
    } catch {
      // Ignore
    }

    try {
      const params = new URLSearchParams(location.search || window.location.search);
      const path = (location.pathname || window.location.pathname).toLowerCase();
      const catParam = params.get('category');
      const isBug = path.includes('bug-report') || path.includes('feedback') || params.get('feedback') === 'true' || catParam === 'bug' || params.get('openFeedback') === 'true';

      if (isBug) {
        setIsOpen(true);
        setCategory(catParam || 'bug');
        setTimeout(() => {
          const input = document.getElementById('fw-message-input');
          if (input) input.focus();
        }, 300);
      }
    } catch {
      // Ignore
    }
  }, [location.pathname, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      notifyError('Please enter your feedback message before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      await feedbackService.submitFeedback({
        category,
        rating,
        message: message.trim(),
        user_name: userName.trim(),
        user_email: userEmail.trim(),
        page_url: window.location.pathname + window.location.search,
      });

      setSubmitted(true);
      notifySuccess('Thank you! Your feedback has been sent to our team.');
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setMessage('');
      }, 1800);
    } catch (err) {
      notifyError(err?.response?.data?.detail || err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fw-wrapper">
      {!isOpen && (
        <button
          type="button"
          className="fw-trigger-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Give feedback"
        >
          <LuMessageSquare size={18} />
          <span>Feedback</span>
        </button>
      )}

      {isOpen && (
        <div className="fw-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="fw-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fw-modal-header">
              <div>
                <h3>Product Feedback</h3>
                <p>Tell us how we can make Unified Workforce better for you.</p>
              </div>
              <button
                type="button"
                className="fw-close-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <LuX size={18} />
              </button>
            </div>

            {submitted ? (
              <div className="fw-success-state">
                <LuCircleCheck size={44} className="fw-success-icon" />
                <h4>Feedback Received!</h4>
                <p>Thank you for sharing your thoughts with us. We read every piece of feedback.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="fw-form">
                <div className="fw-field">
                  <label className="fw-label">Feedback Category</label>
                  <div className="fw-categories-grid">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const active = category === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          className={`fw-category-pill${active ? ' is-active' : ''}`}
                          onClick={() => setCategory(cat.id)}
                        >
                          <Icon size={14} />
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="fw-field">
                  <label className="fw-label">How is your experience?</label>
                  <div className="fw-stars-wrap">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const active = star <= (hoverRating || rating);
                      return (
                        <button
                          key={star}
                          type="button"
                          className={`fw-star-btn${active ? ' is-active' : ''}`}
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          aria-label={`${star} star rating`}
                        >
                          <LuStar size={20} fill={active ? '#f59e0b' : 'none'} color={active ? '#f59e0b' : '#94a3b8'} />
                        </button>
                      );
                    })}
                    <span className="fw-rating-label">
                      {hoverRating || rating} / 5
                    </span>
                  </div>
                </div>

                <div className="fw-field">
                  <label htmlFor="fw-message-input" className="fw-label">Your Feedback</label>
                  <textarea
                    id="fw-message-input"
                    className="fw-textarea"
                    rows={4}
                    placeholder="Describe what you like, bugs you found, or ideas for new features..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>

                <div className="fw-meta-hint">
                  Capturing page: <code>{window.location.pathname}</code>
                </div>

                <div className="fw-actions">
                  <button
                    type="button"
                    className="fw-btn-cancel"
                    onClick={() => setIsOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="fw-btn-submit"
                    disabled={submitting || !message.trim()}
                  >
                    <LuSend size={15} />
                    {submitting ? 'Sending...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
